import {inject, Service} from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {GoogleService} from "./google-service";
import {environment} from "../../environments/environment";
import {Observable} from "rxjs";
import {PartSerialInformation} from "../dialogs/parts-dialog/part-serial-information";
import {DealerConsumerInformation} from "../dialogs/parts-dialog/dealer-consumer-information";
import {ProductInformation} from "../dialogs/parts-dialog/product-information";
import {DecoderData} from "../model/decoder-data";
import {WarrantyClaim} from "../dialogs/parts-dialog/warranty-claim";
import {SewpWarranty} from "../dialogs/parts-dialog/sewp-warranty";
import {Part} from "../products/part";
import {DefectCode} from "../model/defect-code";
import {StdWarranty} from "../dialogs/parts-dialog/std-warranty";
import {ExtWarranty} from "../dialogs/parts-dialog/ext-warranty";
import {Competitor} from "../model/competitor";
import {SpecialQuote} from "../model/special-quote";
import {SpecialQuoteRequest} from "../model/special-quote-request";
import {ExternalSpecial} from "../model/external-special";
import {ExternalSpecialRequestEntry} from "../model/external-special-request-entry";
import {ExternalSpecialLineAssignment} from "../model/external-special-line-assignment";

@Service()
export class SpecialQuoteService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);
  private urlBase = environment.serviceUrl + 'specialquote/';

  public getCompetitors(): Observable<Competitor[]> {
    let url = this.urlBase + 'competitor';
    return this.http.get<Competitor[]>(url, {headers: this.getRequestHeaders()});
  }

  public getSpecialProducts(company: string): Observable<string[]> {
    let url = this.urlBase + 'specialproducts/' + company;
    return this.http.get<string[]>(url, {headers: this.getRequestHeaders()});
  }

  public getSpecialQuote(entityId: string, quoteNumber: number): Observable<SpecialQuote> {
    let url = this.urlBase + 'specialquote/' + entityId + '/' + quoteNumber.toString();
    return this.http.get<SpecialQuote>(url, {headers: this.getRequestHeaders()});
  }

  public insertSpecialQuotesCompetitor(quoteNumber: number, competitorId: number): Observable<string> {
    let url = this.urlBase + 'specialquotescompetitor/' + quoteNumber + '/' + competitorId;
    return this.http.put<string>(url, {headers: this.getPostHeaders()});
  }

  public preProcessingAction(entityId: string, entityRevision: number, processingStatus: string, externalSpecialRequestId: string): Observable<void> {
    let url = this.urlBase + 'preprocessingaction/' + entityId + '/' + entityRevision + '/' + processingStatus + '/' + externalSpecialRequestId;
    return this.http.patch<void>(url, null, {headers: this.getPostHeaders()});
  }

  public applicationEngineerCancel(entityId: string, entityRevision: number, externalId: string): Observable<void> {
    let url = this.urlBase + 'applicationengineercancel/' + entityId + '/' + entityRevision + '/' + externalId;
    return this.http.post<void>(url, null, {headers: this.getPostHeaders()});
  }

  public userCancel(entityId: string, entityRevision: number, externalId: string): Observable<void> {
    let url = this.urlBase + 'usercancel/' + entityId + '/' + entityRevision + '/' + externalId;
    return this.http.post<void>(url, null, {headers: this.getPostHeaders()});
  }

  public finalizeSpecialQuoteRequest(entityId: string, entityRevision: number, externalSpecialRequestId: string): Observable<void> {
    let url = this.urlBase + 'finalizeSpecialQuoteRequest/' + entityId + '/' + entityRevision + '/' + externalSpecialRequestId;
    return this.http.put<void>(url, null, {headers: this.getPostHeaders()});
  }

  public getExternalSpecialRequests(entityId: string, entityRevision: number): Observable<ExternalSpecialRequestEntry[]> {
    let url = this.urlBase + 'externalspecialrequest/' + entityId + '/' + entityRevision;
    return this.http.get<ExternalSpecialRequestEntry[]>(url, {headers: this.getRequestHeaders()});
  }

  public isUserCancelable(entityId: string, entityRevision: number): Observable<boolean> {
    let url = this.urlBase + 'usercancelable/' + entityId + '/' + entityRevision;
    return this.http.get<boolean>(url, {headers: this.getRequestHeaders()});
  }

  public getExternalSpecials(entityId: string, entityRevision: number): Observable<ExternalSpecial[]> {
    let url = this.urlBase + 'externalspecial/' + entityId + '/' + entityRevision;
    return this.http.get<ExternalSpecial[]>(url, {headers: this.getRequestHeaders()});
  }

  public addExternalSpecial(externalSpecial: ExternalSpecial): Observable<void> {
    let url = this.urlBase + 'externalspecial';
    let body = JSON.stringify(externalSpecial);
    return this.http.post<void>(url, body, {headers: this.getPostHeaders()});
  }

  public updateExternalSpecial(externalSpecial: ExternalSpecial): Observable<void> {
    let url = this.urlBase + 'externalspecial';
    let body = JSON.stringify(externalSpecial);
    return this.http.patch<void>(url, body, {headers: this.getPostHeaders()});
  }

  public deleteExternalSpecial(entityId: string, entityRevision: number, externalSpecialId: string): Observable<void> {
    let url = this.urlBase + 'externalspecial/' + entityId + '/' + entityRevision + '/' + externalSpecialId;
    return this.http.delete<void>(url, {headers: this.getPostHeaders()});
  }

  public getExternalSpecialLineAssignments(entityId: string, entityRevision: number): Observable<ExternalSpecialLineAssignment[]> {
    let url = this.urlBase + 'externalspeciallineassignment/' + entityId + '/' + entityRevision;
    return this.http.get<ExternalSpecialLineAssignment[]>(url, {headers: this.getRequestHeaders()});
  }

  /** Replaces every line assignment on the entity revision with the ones given. */
  public saveExternalSpecialLineAssignments(entityId: string, entityRevision: number, assignments: ExternalSpecialLineAssignment[]): Observable<void> {
    let url = this.urlBase + 'externalspeciallineassignment/' + entityId + '/' + entityRevision;
    let body = JSON.stringify(assignments);
    return this.http.put<void>(url, body, {headers: this.getPostHeaders()});
  }

  public updateRequest(entityId: string, entityRevision: number, specialQuoteRequest: SpecialQuoteRequest): Observable<void> {
    let url = this.urlBase + 'updaterequest/' + entityId + '/' + entityRevision;
    let body = JSON.stringify(specialQuoteRequest);
    return this.http.patch<void>(url, body, {headers: this.getPostHeaders()});
  }

  public submitRequest(entityId: string, entityRevision: number, specialQuoteRequest: SpecialQuoteRequest): Observable<void> {
    let url = this.urlBase + 'submitrequest/' + entityId + '/' + entityRevision;
    let body = JSON.stringify(specialQuoteRequest);
    return this.http.post<void>(url, body, {headers: this.getPostHeaders()});
  }

  public getSpecialQuoteRequest(entityId: string, entityRevision: number, quoteNumber: number): Observable<SpecialQuoteRequest> {
    let url = this.urlBase + 'specialquoterequest/' + entityId + '/' + entityRevision + '/' + quoteNumber.toString();
    return this.http.get<SpecialQuoteRequest>(url, {headers: this.getRequestHeaders()});
  }

  private getPostHeaders(): HttpHeaders {
    return this.getRequestHeaders().set("Content-Type", "application/json");
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }
}
